import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  HostListener,
  input,
  inject,
} from '@angular/core';
import { DeviceState } from 'src/app/store/device/device.state';

@Component({
  selector: 'app-right-menu-panel',
  templateUrl: './right-menu-panel.component.html',
  styleUrls: ['./right-menu-panel.component.scss'],
  host: {
    class: 'contents',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class RightMenuPanelComponent implements OnDestroy {
  // --- Injected dependencies ---
  private readonly deviceState = inject(DeviceState);

  /**
   * Right menu expanded state as input signal
   */
  rightMenuExpanded = input<boolean>(false);

  /**
   * Drag state variables for swipe gesture
   */
  private isDragging = false;
  isDismissingViaDrag = false; // Public for potential template access
  private startX = 0;
  private currentX = 0;
  private translateX = 0;

  /**
   * Drag thresholds
   */
  private readonly DRAG_THRESHOLD = 100; // pixels to drag before dismissing
  private readonly VELOCITY_THRESHOLD = 0.5; // pixels per ms for quick swipe

  /**
   * Listen for global mouse move (for drag)
   */
  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.onDragMove(event);
    }
  }

  /**
   * Listen for global mouse up (for drag)
   */
  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isDragging) {
      this.onDragEnd(event);
    }
  }

  /**
   * Listen for global touch move (for drag)
   */
  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (this.isDragging) {
      this.onDragMove(event);
    }
  }

  /**
   * Listen for global touch end (for drag)
   */
  @HostListener('document:touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (this.isDragging) {
      this.onDragEnd(event);
    }
  }

  ngOnDestroy() {}

  /**
   * Handle touch/mouse start for drag gesture
   */
  onDragStart(event: TouchEvent | MouseEvent): void {
    // Only enable drag on mobile when right menu is expanded
    if (!this.deviceState.isSmallerThanLargeScreen() || !this.rightMenuExpanded()) return;

    this.isDragging = true;

    if (event instanceof TouchEvent) {
      this.startX = event.touches[0].clientX;
    } else {
      this.startX = event.clientX;
    }

    this.currentX = this.startX;
  }

  /**
   * Handle touch/mouse move for drag gesture
   */
  onDragMove(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) return;

    if (!this.deviceState.isSmallerThanLargeScreen()) return;

    let clientX: number;
    if (event instanceof TouchEvent) {
      clientX = event.touches[0].clientX;
    } else {
      clientX = event.clientX;
    }

    const deltaX = clientX - this.startX;

    // Only allow dragging right (positive deltaX), not left
    if (deltaX > 0) {
      this.translateX = deltaX;
      this.currentX = clientX;

      // Apply transform to the right menu
      const rightMenuElement = this.getRightMenuElement();
      if (rightMenuElement) {
        // Add resistance effect - the further you drag, the harder it gets
        const resistance = Math.max(0.3, 1 - Math.abs(deltaX) / 500);
        const adjustedDelta = deltaX * resistance;

        rightMenuElement.style.transform = `translateX(${adjustedDelta}px)`;
      }
    }
  }

  /**
   * Handle touch/mouse end for drag gesture
   */
  onDragEnd(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) return;

    if (!this.deviceState.isSmallerThanLargeScreen()) return;

    const deltaX = this.translateX;
    const velocity = this.calculateVelocity();

    const rightMenuElement = this.getRightMenuElement();

    // Determine if we should dismiss or snap back
    // For right swipe, check if dragged right enough
    const shouldDismiss =
      Math.abs(deltaX) > this.DRAG_THRESHOLD ||
      velocity > this.VELOCITY_THRESHOLD;

    if (shouldDismiss) {
      // Set flag to bypass any exit animations if needed
      this.isDismissingViaDrag = true;

      // Dismiss the right menu with fast animation from current position
      if (rightMenuElement) {
        rightMenuElement.style.transition = 'transform 200ms ease-in';
        rightMenuElement.style.transform = 'translateX(100%)';
      }

      // Close after animation completes
      setTimeout(() => {
        // Note: The parent component should handle closing via its UI service
        // We emit an event or the parent listens to this component's state
        this.resetDragState();
        // Reset flag after a delay
        setTimeout(() => {
          this.isDismissingViaDrag = false;
        }, 50);
      }, 200);
    } else {
      // Snap back to original position
      if (rightMenuElement) {
        rightMenuElement.style.transition =
          'transform 250ms cubic-bezier(0.4, 0.0, 0.2, 1)';
        rightMenuElement.style.transform = 'translateX(0)';
      }

      // Reset after animation
      setTimeout(() => {
        this.resetDragState();
      }, 250);
    }

    this.isDragging = false;
  }

  /**
   * Calculate drag velocity for quick swipe detection
   */
  private calculateVelocity(): number {
    // Simple velocity calculation based on last movement
    return Math.abs(this.translateX) / 100; // Normalized velocity
  }

  /**
   * Get the right menu element
   */
  private getRightMenuElement(): HTMLElement | null {
    return document.querySelector('.right-menu-container') as HTMLElement;
  }

  /**
   * Reset drag state and clear inline styles
   */
  private resetDragState(): void {
    this.translateX = 0;
    this.startX = 0;
    this.currentX = 0;

    const rightMenuElement = this.getRightMenuElement();
    if (rightMenuElement) {
      rightMenuElement.style.transition = '';
      rightMenuElement.style.transform = '';
    }
  }
}
